import { useState } from 'react';
import { LogOut, User as UserIcon, Sun, Moon } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { useTheme } from '@/app/theme';
import { Modal } from '@/components/ui/Modal';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Topbar(): JSX.Element {
  const { user, logout, updateProfile } = useAuth();
  const { theme, toggle } = useTheme();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [editName, setEditName] = useState(user?.name ?? '');
  const [editEmail, setEditEmail] = useState(user?.email ?? '');

  const handleSaveProfile = () => {
    updateProfile({ name: editName, email: editEmail });
    setIsEditProfileOpen(false);
  };

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-ink-200/70 dark:border-white/10 bg-white/80 dark:bg-ink-900/80 px-8 backdrop-blur-md">
      <div className="font-mono text-[11px] font-medium tracking-[0.18em] text-ink-400 dark:text-ink-500">
        ENTERPRISE DATA GOVERNANCE
      </div>

      <div className="flex items-center gap-3">
        {user && (
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-400 to-brand-700 text-[11px] font-semibold text-white shadow-glow">
                {initials(user.name)}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-2 border-white bg-brand-400" />
            </div>
            <div className="hidden leading-tight sm:block">
              <div className="text-sm font-medium text-ink-900 dark:text-ink-50">{user.name}</div>
              <div className="font-mono text-[10px] tracking-wider text-ink-500 dark:text-ink-400">{user.email}</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1.5 ml-2">
          <button
            type="button"
            onClick={toggle}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-500 dark:text-ink-400 transition hover:bg-ink-100 dark:hover:bg-white/5 hover:text-ink-800 dark:hover:text-ink-50"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditName(user?.name ?? '');
              setEditEmail(user?.email ?? '');
              setIsEditProfileOpen(true);
            }}
            title="Edit profile"
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-500 dark:text-ink-400 transition hover:bg-ink-100 dark:hover:bg-white/5 hover:text-ink-800 dark:hover:text-ink-50"
          >
            <UserIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Edit Profile</span>
          </button>
          <button
            type="button"
            onClick={logout}
            title="Sign out"
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-ink-500 dark:text-ink-400 transition hover:bg-ink-100 dark:hover:bg-white/5 hover:text-ink-800 dark:hover:text-ink-50"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>

      <Modal
        open={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
        title="Edit Profile"
        description="Update your personal information."
        footer={
          <>
            <button
              onClick={() => setIsEditProfileOpen(false)}
              className="rounded-lg px-3 py-1.5 text-sm text-ink-500 transition hover:text-ink-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              className="btn-primary px-4 py-1.5 text-sm"
            >
              Save Changes
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-ink-700 dark:text-ink-200">Name</span>
            <input
              type="text"
              className="h-10 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 px-3 text-sm shadow-soft focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-ink-700 dark:text-ink-200">Email</span>
            <input
              type="email"
              className="h-10 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 px-3 text-sm shadow-soft focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
            />
          </label>
        </div>
      </Modal>
    </header>
  );
}
